import React, { forwardRef, PureComponent } from "react";
import ReactTableComp from "react-table";
import { get, size } from "lodash";
import "react-table/react-table.css";
import styled from "styled-components";
import PropTypes from "prop-types";
import { TableLoader } from ".";
import { TABLE_CONFIG } from "../constant";

const NoDataComponent = ({ loading }) => {
  return loading ? null : (
    <div className="no-record-found"> No records found</div>
  );
};

const LoaderComponent = props => <TableLoader {...props} />;

NoDataComponent.propTypes = {
  loading: PropTypes.bool
};

NoDataComponent.defaultProps = {
  loading: false
};

const getNoDataProps = props => ({
  loading: props.loading
});

const PMIICTable = forwardRef(
  ({ className, columns, data, loading, ...rest }, ref) => {
    return (
      <div className={className} ref={ref}>
        <ReactTableComp
          data={data}
          columns={columns}
          loading={loading}
          LoadingComponent={LoaderComponent}
          showPagination={false}
          minRows={0}
          getNoDataProps={getNoDataProps}
          NoDataComponent={NoDataComponent}
          defaultPageSize={TABLE_CONFIG.ROWS}
          sortable={false}
          getTrProps={(state, { index }) => {
            return {
              onClick: () => {
                if (data.length === index + 1) {
                  setTimeout(() => {
                    const scrollDiv = document.querySelector(".rt-tbody");
                    if (scrollDiv) {
                      scrollDiv.scrollTo(0, scrollDiv.scrollHeight);
                    }
                  }, 0);
                }
              }
            };
          }}
          {...rest}
        />
      </div>
    );
  }
);

PMIICTable.propTypes = {
  original: PropTypes.shape({}),
  className: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})),
  loading: PropTypes.bool,
  SubComponent: PropTypes.func
};

PMIICTable.defaultProps = {
  className: "",
  original: {},
  data: [],
  loading: false,
  SubComponent: null
};

// eslint-disable-next-line react/no-multi-comp
class ReactTableCompRef extends PureComponent {
  state = {
    offsetHeight: 0
  };

  tableRef = React.createRef();

  componentDidMount() {
    this.setState({
      offsetHeight: get(this.tableRef, "current.offsetTop", 0)
    });
  }

  customFilter = (filter, row) => {
    const id = filter.pivotId || filter.id;
    if (row[id] !== null && typeof row[id] === "string") {
      return row[id] !== undefined
        ? String(row[id].toLowerCase()).includes(filter.value.toLowerCase())
        : true;
    }
    return null;
  };

  render() {
    const { offsetHeight } = this.state;
    const { data } = this.props;
    const windowHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight;
    const tableHeight = size(data) > 14 ? windowHeight - offsetHeight : "auto";
    const reactTableClientWidth = get(
      document.querySelector(".ReactTable"),
      "clientWidth"
    );
    setTimeout(() => {
      const tBodyClientWidth = get(
        document.querySelector(".rt-tbody"),
        "clientWidth"
      );
      const trHead = document.querySelector(".rt-thead.-header .rt-tr");
      const scrollWidth = reactTableClientWidth - tBodyClientWidth;
      if (trHead && scrollWidth) {
        trHead.style.paddingRight = "15px";
      }
    }, 500);

    return (
      <PMIICTable
        ref={this.tableRef}
        defaultFilterMethod={this.customFilter}
        style={{ height: tableHeight }}
        {...this.props}
      />
    );
  }
}

ReactTableCompRef.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({}))
};

ReactTableCompRef.defaultProps = {
  data: []
};

export const ReactTable = styled(ReactTableCompRef)`
  .rt-table {
    background-color: ${/* istanbul ignore next */ ({ theme }) =>
      theme.palette.WHITE};
  }
  .rt-thead {
    min-width: 1024px;
    box-shadow: none !important;
    .rt-tr {
      background-color: ${/* istanbul ignore next */ ({ theme }) =>
        theme.palette.DARK_GREY};
      font-family: ${/* istanbul ignore next */ ({ theme }) =>
        theme.fontFamily.BOLD};
      > div {
        border-right: none;
        text-align: left;
        color: ${/* istanbul ignore next */ ({ theme }) => theme.palette.WHITE};
      }
    }
  }
  .rt-td {
    border: none !important;
    display: flex;
    align-items: center;
    justify-content: left;
  }
  .word-wrap {
    white-space: normal !important;
    word-break: break-all !important;
  }
  .rt-tbody {
    .rt-tr-group {
      border-right: none;
      .-even {
        background-color: ${/* istanbul ignore next */ ({ theme }) =>
          theme.palette.ALTERNATIVE_ROW};
        + .sub-container {
          margin-top: 17px;
        }
      }
    }
  }
  .expandable-cell {
    display: flex;
    align-items: center;
  }
  .expandable-col-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    font-size: ${/* istanbul ignore next */ ({ theme }) =>
      theme.fontSize.MEDIUM};
    color: ${/* istanbul ignore next */ ({ theme }) => theme.palette.SECONDARY};
    font-family: ${/* istanbul ignore next */ ({ theme }) =>
      theme.fontFamily.REGULAR};
  }
  .name-column {
    color: ${/* istanbul ignore next */ ({ theme }) => theme.palette.SECONDARY};
  }
  .icon-holder {
    color: ${/* istanbul ignore next */ ({ theme }) => theme.palette.PRIMARY};
    height: 20px;
    > span {
      display: inline-block;
      padding-right: 5px;
    }
    svg {
      font-size: 20px;
    }
  }
  .transfer-icon {
    margin: 0 10px;
    svg {
      font-size: 25px;
      path {
        fill: ${/* istanbul ignore next */ ({ theme }) =>
          theme.palette.SECONDARY};
      }
    }
  }
  .edit-icon {
    cursor: pointer;
    float: left;
    margin-right: 10px;
    margin-top: 5px;
    svg {
      font-size: 20px;
      path {
        fill: ${/* istanbul ignore next */ ({ theme }) =>
          theme.palette.SECONDARY};
      }
    }
  }
  .delete-icon {
    cursor: pointer;
    svg {
      font-size: 20px;
    }
    path {
      fill: none;
    }
    g {
      path {
        fill: ${/* istanbul ignore next */ ({ theme }) =>
          theme.palette.SECONDARY};
      }
    }
  }
  .block-icon {
    position: relative;
    top: 2px;
    svg {
      font-size: 18px;
      color: ${/* istanbul ignore next */ ({ theme }) =>
        theme.palette.NEGATIVE};
    }
    cursor: pointer;
  }
  .active-contact-icon {
    cursor: pointer;
    svg {
      #path {
        fill: ${/* istanbul ignore next */ ({ theme }) =>
          theme.palette.SECONDARY};
      }
      #stroke {
        stroke: ${/* istanbul ignore next */ ({ theme }) =>
          theme.palette.SECONDARY};
      }
    }
  }
  .sub-container {
    display: flex;
    padding-bottom: 20px;
  }
  .expanded-subComponent {
    margin-left: 30px;
    margin-right: 30px;
    border: 1px dashed
      ${/* istanbul ignore next */ ({ theme }) => theme.palette.PRIMARY};
    border-radius: 4px;
    background-color: ${/* istanbul ignore next */ ({ theme }) =>
      theme.palette.ACTIVE_LIGHT};
    display: flex;
    align-self: flex-start;
    align-items: center;
    padding: 10px 15px;
    flex-wrap: wrap;
  }
  .row-tr {
    padding: 5px 10px 5px 0;
    &:last-child {
      padding-right: 0px;
    }
  }
  .label-tr {
    margin-right: 5px;
    color: ${/* istanbul ignore next */ ({ theme }) => theme.palette.DARKGREY2};
  }
  .rt-tr-group {
    min-height: 50px;
    ._even {
      + .sub-container {
        background-color: ${/* istanbul ignore next */ ({ theme }) =>
          theme.palette.ALTERNATIVE_ROW};
      }
    }
  }
  .no-record-found {
    display: flex;
    min-height: 200px;
    justify-content: center;
    align-items: center;
  }
  .rt-td {
    white-space: normal !important;
  }
`;
